import type { ReactNode } from 'react';
import type { AuthState } from '../../types';

import i18next from 'i18next';
import { useSetState } from 'minimal-shared/hooks';
import { useMemo, useEffect, useCallback } from 'react';

import axios, { endpoints } from 'src/lib/axios';
import { clearRoleSpecificCache } from 'src/lib/swr';
import { setSentryUser, clearSentryUser } from 'src/lib/sentry';

import * as authActions from './action';
import { JWT_STORAGE_KEY } from './constant';
import { AuthContext } from '../auth-context';
import { setSession, isValidToken, extractAssumedRoleId } from './utils';

type Props = {
  children: ReactNode;
};

export function AuthProvider({ children }: Readonly<Props>) {
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
    assumedRoleId: null,
    isSwitchingRole: false,
  });

  const setUserLanguage = useCallback((language?: 'DE' | 'FR') => {
    if (language) {
      const i18nLanguage = language.toLowerCase();
      localStorage.setItem('i18nextLng', i18nLanguage);
      i18next.changeLanguage(i18nLanguage);
    }
  }, []);

  const checkUserSession = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem(JWT_STORAGE_KEY);

      if (accessToken && isValidToken(accessToken)) {
        setSession(accessToken);

        const res = await axios.get(endpoints.auth.me);

        const user = res.data;

        setUserLanguage(user.language);

        const assumedRoleId = extractAssumedRoleId(accessToken);

        setSentryUser(user, assumedRoleId);

        setState({
          user: { ...user, accessToken },
          loading: false,
          assumedRoleId,
        });
      } else {
        if (accessToken) {
          setSession(null);
        }
        clearSentryUser();
        setState({ user: null, loading: false, assumedRoleId: null });
      }
    } catch (error) {
      console.error(error);
      clearSentryUser();
      setState({ user: null, loading: false, assumedRoleId: null });
    }
  }, [setState, setUserLanguage]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const assumeRole = useCallback(
    async (roleId: number) => {
      setState({ isSwitchingRole: true });
      try {
        const assumedRoleId = await authActions.assumeRole(roleId);

        if (assumedRoleId !== null) {
          setSentryUser(state.user, assumedRoleId);
          setState({ assumedRoleId });
          await clearRoleSpecificCache();
        }
      } catch (error) {
        console.error('Failed to assume role:', error);
        throw error;
      } finally {
        setState({ isSwitchingRole: false });
      }
    },
    [setState, state.user]
  );

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user ? { ...state.user } : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      assumedRoleId: state.assumedRoleId,
      isSwitchingRole: state.isSwitchingRole,
      assumeRole,
    }),
    [checkUserSession, state.user, state.assumedRoleId, state.isSwitchingRole, status, assumeRole]
  );

  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
